import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { PlaySectionName, ValidationService, ValidationStatus } from '@r1-compass-library/features/validation-sync';
import { DivisionConfig, GenericSelectionCriterionData, Play } from '@r1-compass-library/models';
import { PlayBuilderForm } from './play-record-selection/play-record-play-builder/play-builder-form.model';
import {
    addAdditionalFieldFormGroups,
    convertDomainToForm,
    DefinitionForm,
} from './play-definition/play-definition-form.service';
import { PlayFileImportForm } from '@r1-compass-library/models/play/play-file-import-form.model';
import { initForm, PlayTalkingPointsForm } from './play-talking-points/play-talking-points-form.service';
import { PlaySchedulingForm } from '@r1-compass-library/models/play/play-scheduling-form.model';
import { populatePlayBuilderForm } from './play-record-selection/play-record-play-builder/play-builder-form.service';
import { populatePlayImporterForm } from './play-record-selection/play-record-play-importer/play-importer-form.service';
import { DisableValue1AndValue2Conditionally } from '@r1-compass-library/features/validation-sync/disable-value1-and-value2-conditionally.service';

export type PlayValidationStatus = {
    erroredSections: Set<PlaySectionName>;
};

/** Asynchronously loaded state that play validation depends on. */
export type PlayValidationContext = {
    unavailableLifecycleRulesetIds: Set<number>;
    unavailableTemplateCriteriaIds: Set<number>;
    unavailableLibraryContentIds: Set<number>;
    usedLibraryContentIds: Set<number>;
    multipleRecommendationsEnabled: boolean;
    selectedDivisionConfig: DivisionConfig;
};

export type PlayFormMap = {
    definition: FormGroup<DefinitionForm>;
    recordSelection: FormGroup<PlayBuilderForm> | FormGroup<PlayFileImportForm>;
    talkingPoints: FormGroup<PlayTalkingPointsForm>;
    scheduling: FormGroup<PlaySchedulingForm>;
};
type PlayFormMapTypes = PlayFormMap[keyof PlayFormMap];

export function isDefinitionForm(form: PlayFormMapTypes): form is FormGroup<DefinitionForm> {
    return !!(form as FormGroup<DefinitionForm>).controls.additionalFields;
}
export function isRecordSelectionPlayBuilderForm(form: PlayFormMapTypes): form is FormGroup<PlayBuilderForm> {
    return !!(form as FormGroup<PlayBuilderForm>).controls.templateCriteriaId;
}
export function isRecordSelectionPlayImporterForm(form: PlayFormMapTypes): form is FormGroup<PlayFileImportForm> {
    return !!(form as FormGroup<PlayFileImportForm>).controls.salesforceUniqueFieldSourceName;
}
export function isTalkingPointsForm(form: PlayFormMapTypes): form is FormGroup<PlayTalkingPointsForm> {
    return !!(form as FormGroup<PlayTalkingPointsForm>).controls.froalaContentFormControl;
}
export function isSchedulingForm(form: PlayFormMapTypes): form is FormGroup<PlaySchedulingForm> {
    return !!(form as FormGroup<PlaySchedulingForm>).controls.daysOfWeek;
}

@Injectable({
    providedIn: 'root',
})
export class PlayValidationService
    implements ValidationService<PlaySectionName, Play, PlayFormMap, PlayValidationContext>
{
    readonly #fb = inject(NonNullableFormBuilder);

    public validateSection<K extends PlaySectionName>(
        form: PlayFormMap[K],
        section: K,
        context: PlayValidationContext
    ): ValidationStatus<PlaySectionName> {
        const validationStatus: PlayValidationStatus = { erroredSections: new Set() };

        if (isDefinitionForm(form)) {
            if (this.validateDefinitionForm(form, context)) validationStatus.erroredSections.add(section);
        }
        if (isRecordSelectionPlayBuilderForm(form)) {
            if (this.validateRecordSelectionPlayBuilderForm(form, context))
                validationStatus.erroredSections.add(section);
        }
        if (isRecordSelectionPlayImporterForm(form)) {
            if (this.validateRecordSelectionFileImportForm(form)) validationStatus.erroredSections.add(section);
        }
        if (isTalkingPointsForm(form)) {
            if (this.validateTalkingPointsForm(form, context)) validationStatus.erroredSections.add(section);
        }

        return validationStatus;
    }

    public validateModel(model: Play, context: PlayValidationContext): ValidationStatus<PlaySectionName> {
        const validationStatus: PlayValidationStatus = { erroredSections: new Set() };

        // Definition
        this.validateModelBySection('definition', model, validationStatus, context);
        // Record Selection (Builder/Importer)
        this.validateModelBySection('recordSelection', model, validationStatus, context);
        // Talking Points
        this.validateModelBySection('talkingPoints', model, validationStatus, context);

        return validationStatus;
    }

    private validateDefinitionForm(form: FormGroup<DefinitionForm>, context: PlayValidationContext): boolean {
        if (form.getRawValue().recommendationGeneratorType === 'Builder') {
            form.controls.recommendationLifecycleRulesetId.addValidators(Validators.required);
            form.controls.recommendationLifecycleRulesetId.updateValueAndValidity({ emitEvent: false });
        }

        const formInvalid = form.invalid;
        const recommendationLifecycleRuleUnavailable = context.unavailableLifecycleRulesetIds.has(
            form.getRawValue().recommendationLifecycleRulesetId ?? 0
        );

        return formInvalid || recommendationLifecycleRuleUnavailable;
    }

    private validateRecordSelectionPlayBuilderForm(
        form: FormGroup<PlayBuilderForm>,
        context: PlayValidationContext
    ): boolean {
        const formInvalid = form.invalid;
        const templateCriteriaUnavailable = context.unavailableTemplateCriteriaIds.has(
            form.getRawValue().templateCriteriaId ?? 0
        );
        return formInvalid || templateCriteriaUnavailable;
    }

    private validateRecordSelectionFileImportForm(form: FormGroup<PlayFileImportForm>): boolean {
        // Not dependent on any entities
        return form.invalid;
    }

    private validateTalkingPointsForm(
        form: FormGroup<PlayTalkingPointsForm>,
        context: PlayValidationContext,
        usedContentAssetIds?: Set<number>
    ): boolean {
        const formInvalid = form.invalid;
        const contentIdsToCheck = usedContentAssetIds ?? context.usedLibraryContentIds;
        const anyUsedContentUnavailable = [...contentIdsToCheck].some(id =>
            context.unavailableLibraryContentIds.has(id)
        );

        return formInvalid || anyUsedContentUnavailable;
    }

    private validateModelBySection(
        type: PlaySectionName,
        model: Play,
        validationStatus: PlayValidationStatus,
        context: PlayValidationContext
    ) {
        const divisionConfig = context.selectedDivisionConfig;

        if (type === 'definition') {
            const definitionForm = this.#fb.group(new DefinitionForm());

            if (divisionConfig) {
                addAdditionalFieldFormGroups(definitionForm, divisionConfig.config?.mapping?.additionalFields ?? []);

                definitionForm.patchValue(convertDomainToForm(model, divisionConfig));
            }
            if (this.validateDefinitionForm(definitionForm, context)) {
                validationStatus.erroredSections.add('definition');
            }
        } else if (type === 'recordSelection') {
            const multipleRecommendationsEnabled = context.multipleRecommendationsEnabled;
            const recordSelectionPlayBuilderForm = this.#fb.group(new PlayBuilderForm());
            const recordSelectionPlayImporterForm = this.#fb.group(new PlayFileImportForm());
            const overrideToggleControl = new FormControl(false);

            if (model.type === 'Builder') {
                const usedDatamartFields = model.config?.criteria?.selectionCriteria
                    ?.filter(c => c.type === 'generic' || c.type === 'model')
                    .map(c => (c.data as GenericSelectionCriterionData).dataMartField)
                    .filter(field => !!field);

                if (multipleRecommendationsEnabled !== undefined) {
                    populatePlayBuilderForm(
                        recordSelectionPlayBuilderForm,
                        overrideToggleControl,
                        model,
                        multipleRecommendationsEnabled
                    );

                    DisableValue1AndValue2Conditionally.disablePlays(
                        recordSelectionPlayBuilderForm,
                        usedDatamartFields
                    );
                }
                if (this.validateRecordSelectionPlayBuilderForm(recordSelectionPlayBuilderForm, context)) {
                    validationStatus.erroredSections.add('recordSelection');
                }
            } else {
                populatePlayImporterForm(
                    recordSelectionPlayImporterForm,
                    model.config?.playFileImport,
                    divisionConfig?.config?.mapping?.salesforcePlayObject,
                    divisionConfig?.config?.mapping?.salesforceRecommendationObject
                );
                if (this.validateRecordSelectionFileImportForm(recordSelectionPlayImporterForm)) {
                    validationStatus.erroredSections.add('recordSelection');
                }
            }
        } else if (type === 'talkingPoints') {
            const talkingPointsForm = this.#fb.group(new PlayTalkingPointsForm());
            const talkingPointsDef = model.config?.talkingPointsDefinition;
            const fileHeaders = model.config?.playFileImport?.headers ?? [];

            initForm(talkingPointsForm, talkingPointsDef, fileHeaders);

            const playContentAssetIds = new Set(
                (talkingPointsDef?.contentAssets ?? []).map(ca => ca.id).filter((id): id is number => id !== undefined)
            );

            if (this.validateTalkingPointsForm(talkingPointsForm, context, playContentAssetIds)) {
                validationStatus.erroredSections.add('talkingPoints');
            }
        }
    }
}
